import {AxiosError} from 'axios'
import {SUPPORT_PHONE_LABEL} from '../../../../_metronic/helpers/supportContact'

// What «اعلان‌های صورتحساب» and «گزارش دوره‌ای» share: the SMS price block, SMS outcomes, the admin's
// service tariffs and the {code, message} errors of api/business-services.

/** The wallet's SMS price for the business, the same block as «لینک خریداران». */
export type ServiceSms = {
  /** Rials per SMS part; 0 while the administrator has not set a price (SMS closed). */
  partPrice: number
  balance: number
  /** On the administrator's «پیامک بدون کسر از کیف پول» list: SMS is not charged. */
  withoutCharge: boolean
  chargeEnabled: boolean
}

/** A sample SMS the server built: its text, its parts and the cost for one recipient. */
export type SmsSample = {body: string; parts: number; cost: number}

export type SmsOutcomeState = 'none' | 'pending' | 'sent' | 'skipped' | 'failed'

/** What happened to the SMS of an alert or a report run. */
export type SmsOutcome = {
  state: SmsOutcomeState
  title: string
  reason?: string | null
  parts: number
  cost: number
  recipients: number
  body?: string | null
}

export type ServiceFeeState = 'charged' | 'unpaid' | 'free'

/** The service tariff of one alert or run, separate from the SMS cost. */
export type ServiceFee = {state: ServiceFeeState; amount: number}

/**
 * «ترکیبی» (backend 1.0.0.640+): an activation fee and a fee of each use. «تماس بگیرید» (contact): the business cannot
 * turn the service on itself; only a business an administrator gave access to (free, until a day) uses it.
 */
export type TariffMode = 'free' | 'oneTime' | 'perUse' | 'combined' | 'contact'

/** How a business pays for a «ترکیبی» service (backend 1.0.0.690+): the subscription, or the fee of each use. */
export type BillingMethod = 'subscription' | 'perUse'

/** What a free allowance is counted over. */
export type QuotaScope = 'total' | 'operation' | 'day' | 'month'

/**
 * What the business's free list, gifts and best discount code make of a tariff (BusinessServicePricing.FeatureView);
 * null when the feature is free anyway.
 */
export type TariffPricing = {
  exempt: boolean
  note?: string | null
  gift?: {title: string; quantity: number; remaining: number; periodLabel: string; note: string} | null
  discount?: {code: string; title: string; amount: number; endsAtLabel?: string | null; note: string} | null
}

/** buyerReaction · article6 · sendError · batchSent · periodicReport; the server names each. */
/** One period a subscription can be bought for, with what it takes for this business (2026-10-18). */
export type ServicePeriod = {
  months: number
  title?: string | null
  /** The tariff's fee of the period and its price before the offer. */
  amount: number
  listAmount?: number | null
  /** What it takes for this business after its free list, gifts and codes, and why. */
  due: number
  note?: string | null
  /** The fee spread over its months, for comparing. */
  perMonth: number
  /** The tariff's own period. */
  own: boolean
  /** The period the business's activation was bought for. */
  bought: boolean
}

export type ServiceTariff = {
  feature: string
  label: string
  mode: TariffMode
  amount: number
  /** «قیمت قبل از تخفیف» (backend 1.0.0.639+): shown struck through; only when it is above the price. */
  listAmount?: number | null
  /** The online payment's commission: a percentage of each payment within a minimum and a maximum. */
  usePercent?: number | null
  useMinimum?: number | null
  useMaximum?: number | null
  /** The providers whose own commission replaces the one above, for this business (backend 1.0.0.675+). */
  providers?: {providerCode: string; providerName: string; description: string}[] | null
  /**
   * Every period the subscription can be bought for, shortest first, when the tariff offers more than its own (backend
   * 1.0.0.675+); null otherwise.
   */
  periods?: ServicePeriod[] | null
  /** The period the business's activation was bought for when it is not the tariff's own. */
  periodMonths?: number | null
  /** How many uses are free first (the users of «کاربر اضافه»). */
  freeQuota?: number | null
  /** What one use is («هر پرداخت موفق خریدار»). */
  useMeaning?: string | null
  // Each part on its own (backend 1.0.0.640+): the activation fee (for ever, or a subscription of some months) and the
  // fee of each use, each with its price before an offer; the free allowance and the daily limit of bulk operations.
  activationAmount?: number | null
  activationListAmount?: number | null
  activationMonths?: number | null
  /** «ماهانه»، «سالانه» …; null for ever. */
  activationPeriod?: string | null
  useAmount?: number | null
  useListAmount?: number | null
  freeQuotaScope?: QuotaScope | null
  /** «۵۰ مورد اول هر عملیات رایگان» … */
  freeQuotaText?: string | null
  dailyLimit?: number | null
  usedToday?: number | null
  usedThisMonth?: number | null
  /** When a subscription runs out (null for ever), and whether it has. */
  expiresAt?: string | null
  expiresAtLabel?: string | null
  expired?: boolean | null
  activated: boolean
  /**
   * A service the business turns on itself whatever the tariff (backend 1.0.0.738+): «فعال‌سازی» is offered even when
   * it is free, since nothing happens until it is on («پیامک اعلان‌ها», «استعلام پرونده مالیاتی خریدار»).
   */
  optIn?: boolean | null
  /**
   * The service is off for the business (backend 1.0.0.651+): switched off by an administrator (the business cannot
   * turn it on again until allowed; reason is the administrator's) or by the business itself (its own «فعال‌سازی»
   * turns it on again at the day's tariff). message is the sentence to show.
   */
  switchedOff?: {
    byAdministrator: boolean
    reason?: string | null
    atLabel?: string | null
    message: string
  } | null
  activatedAt?: string | null
  activatedAtLabel?: string | null
  description?: string | null
  pricing?: TariffPricing | null
  /** What the business's packages give this service (backend 1.0.0.641+); null without one. */
  package?: {
    titles: string[]
    coversActivation: boolean
    coveredUntilLabel?: string | null
    includedLeft?: number | null
    includedText?: string | null
  } | null
  /**
   * «تماس بگیرید» (mode contact): only support turns the service on. `granted` is a live access an administrator gave
   * (activated says the same, expiresAt is its end; expired: it ran out). contactMessage is the sentence to show while
   * the business has no live access, null while it has. A free allowance (freeQuotaText) works without an access.
   */
  contact?: boolean | null
  contactMessage?: string | null
  granted?: boolean | null
  /**
   * Only an administrator turns it on and off (backend 1.0.0.758+, «تعریف زنجیرهٔ تأمین برق»): no tariff, no end, no
   * «فعال‌سازی» nor «غیرفعال کردن» for the business, only its request to ASA. activated is the administrator's grant.
   */
  adminOnly?: boolean | null
  /**
   * «ترکیبی» (backend 1.0.0.690+, user request 2026-10-20: «روش‌ها جایگزین هم هستن»): how the business pays — the
   * subscription (its uses are free while it holds) or the fee of each use; null while it has not chosen, or once its
   * subscription ran out. chooseMethod: the service works only after it chooses. autoRenew: «تمدید خودکار» of its
   * subscription from the wallet (null without one). Absent on an older backend, where «ترکیبی» takes both.
   */
  method?: BillingMethod | null
  chooseMethod?: boolean | null
  autoRenew?: boolean | null
}

/** A «ترکیبی» tariff whose two ways the business picks from (backend 1.0.0.690+ says so with chooseMethod). */
export const offersMethods = (tariff?: ServiceTariff | null) =>
  Boolean(tariff && tariff.mode === 'combined' && tariff.chooseMethod !== undefined && tariff.chooseMethod !== null)

/** A «تماس بگیرید» service: the business cannot turn it on itself. */
export const isContactTariff = (tariff?: Pick<ServiceTariff, 'mode' | 'contact'> | null): boolean =>
  Boolean(tariff && (tariff.mode === 'contact' || tariff.contact))

/**
 * A «تماس بگیرید» service the business has no live access to: support turns it on, or renews it. A service still on
 * from a package or an earlier activation (activated) needs nothing.
 */
export const needsContact = (
  tariff?:
    | (Pick<ServiceTariff, 'mode' | 'contact' | 'granted'> & {activated?: boolean | null})
    | null
): boolean => isContactTariff(tariff) && !tariff?.granted && !tariff?.activated

/** What a business reads about a «تماس بگیرید» service it has no access to: the server's sentence, or the same in short. */
export const contactSentence = (
  tariff: Pick<ServiceTariff, 'label' | 'contactMessage' | 'expired' | 'expiresAtLabel'>
): string =>
  tariff.contactMessage?.trim() ||
  (tariff.expired
    ? `دسترسی به «${tariff.label}»${
        tariff.expiresAtLabel ? ` در ${tariff.expiresAtLabel}` : ''
      } به پایان رسید؛ برای تمدید، درخواست ثبت کنید.`
    : `«${tariff.label}» با درخواست شما و از سوی پشتیبانی فعال می‌شود؛ برای استفاده، درخواست فعال‌سازی ثبت کنید.`)

/** The support line after a sentence that sends the business to support, for a page that shows plain text. */
export const withSupportPhone = (text: string): string =>
  text.includes(SUPPORT_PHONE_LABEL)
    ? text
    : // An isolated run: the number keeps its order inside the Persian sentence.
      `${text.trim()} تلفن پشتیبانی: ⁦${SUPPORT_PHONE_LABEL}⁩`

/** The line under a tariff: free for the business, the gifts left, or its discount code; undefined for none. */
export const tariffPricingNote = (tariff: ServiceTariff): string | undefined => {
  const pricing = tariff.pricing
  if (!pricing) return undefined
  if (pricing.exempt) return pricing.note || 'رایگان برای این کسب‌وکار'
  const notes = [pricing.gift?.note, pricing.discount?.note].filter((note): note is string => Boolean(note))
  return notes.length ? notes.join('؛ ') : undefined
}

/** One SMS section the business can turn on: its name and a sample line. */
export type SectionOption = {
  key: string
  label: string
  example?: string | null
  group?: string | null
}

export type FilterSummaryItem = {label: string; value: string}

/** A fee to confirm: its amount after a discount, the tariff before it, and the discount's note. */
/** The activation fee and the fee of each use of a tariff, from its parts or (an older backend) its mode and amount. */
export const tariffParts = (tariff: ServiceTariff) => {
  const activation =
    tariff.activationAmount ?? (tariff.mode === 'oneTime' ? tariff.amount : 0)
  const use = tariff.useAmount ?? (tariff.mode === 'perUse' ? tariff.amount : 0)
  return {
    activation: activation || 0,
    activationList:
      tariff.activationListAmount ?? (tariff.mode === 'oneTime' ? tariff.listAmount ?? null : null),
    period: tariff.activationPeriod || null,
    use: use || 0,
    useList: tariff.useListAmount ?? (tariff.mode === 'perUse' ? tariff.listAmount ?? null : null),
  }
}

/** «اشتراک سالانه»، «فعال‌سازی یک‌باره (همیشگی)». */
export const activationTitle = (period?: string | null) =>
  period ? `اشتراک ${period}` : 'فعال‌سازی یک‌باره (همیشگی)'

export type ChargeItem = {
  feature: string
  label: string
  amount: number
  baseAmount?: number
  note?: string | null
  /** A subscription's period («ماهانه»، «سالانه»; backend 1.0.0.643+): it must be renewed when it ends. */
  period?: string | null
  /** The renewal of a subscription that ran out (backend 1.0.0.643+). */
  renewal?: boolean | null
}

/** A subscription or its renewal among the items (the server says so since 1.0.0.643; older ones only in the label). */
export const chargeKind = (items: ChargeItem[]): 'activation' | 'subscription' | 'renewal' =>
  items.some((item) => item.renewal || /^تمدید/.test(item.label))
    ? 'renewal'
    : items.some((item) => item.period || /^اشتراک/.test(item.label))
    ? 'subscription'
    : 'activation'

// ---- errors ----------------------------------------------------------------------------------------------

export const SERVICE_UNAVAILABLE = 'BUSINESS_SERVICES_UNAVAILABLE'
export const SERVICE_INVALID = 'BUSINESS_SERVICE_INVALID'
export const SERVICE_FORBIDDEN = 'BUSINESS_SERVICE_FORBIDDEN'
export const CHARGE_CONFIRM = 'BUSINESS_SERVICE_CHARGE_CONFIRM'
/** A bulk operation past the business's daily limit of the service (backend 1.0.0.640+). */
export const DAILY_LIMIT = 'BUSINESS_SERVICE_DAILY_LIMIT'
export const WALLET_INSUFFICIENT = 'WALLET_INSUFFICIENT_BALANCE'
export const REPORT_LIMIT = 'BUSINESS_REPORT_LIMIT'
/**
 * 409: a save would turn on a «تماس بگیرید» service the business has no live access to (backend contact pricing). No fee
 * to confirm and no wallet to charge: only support can give the access.
 */
export const CONTACT_REQUIRED = 'BUSINESS_SERVICE_CONTACT_REQUIRED'

export type ServiceError = {
  status?: number
  code?: string
  message: string
  /** BUSINESS_SERVICE_INVALID: the field the message is about («autoApprove.sellers», AUTO_APPROVE_CONFIRM …). */
  field?: string
  /** BUSINESS_SERVICE_CHARGE_CONFIRM: the one-time fees the save would charge. */
  amount?: number
  items?: ChargeItem[]
  /** WALLET_INSUFFICIENT_BALANCE. */
  balance?: number
  required?: number
  /**
   * BUSINESS_SERVICE_CONTACT_REQUIRED: the service only support turns on; `ended` when the business's access ran out.
   * `message` ends with the support line (every page shows it as it is); `text` is the server's sentence alone, for a
   * page that shows the support line as a call link of its own.
   */
  contact?: {feature?: string; ended: boolean; text: string}
}

type ErrorBody = {
  code?: string
  message?: string
  Message?: string
  field?: string
  amount?: number
  items?: ChargeItem[]
  balance?: number
  required?: number
  feature?: string
  ended?: boolean
}

const CONTACT_FALLBACK =
  'این سرویس با درخواست شما و از سوی پشتیبانی فعال می‌شود؛ برای استفاده، درخواست فعال‌سازی ثبت کنید.'

const NETWORK_MESSAGE =
  'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'

export const readServiceError = (error: unknown, fallback: string): ServiceError => {
  const response = (error as AxiosError<ErrorBody> | undefined)?.response
  if (!response) return {code: 'NETWORK', message: NETWORK_MESSAGE}

  const data = response.data && typeof response.data === 'object' ? response.data : undefined
  const message = data?.message || data?.Message
  const serverText = typeof message === 'string' && message.trim() ? message : undefined
  if (data?.code === CONTACT_REQUIRED) {
    const text = serverText || CONTACT_FALLBACK
    // The request for the service follows the sentence where it is shown (ServiceRequestLink), not a phone number.
    return {
      status: response.status,
      code: data.code,
      message: text,
      field: typeof data.field === 'string' && data.field ? data.field : undefined,
      contact: {
        feature: typeof data.feature === 'string' && data.feature ? data.feature : undefined,
        ended: data.ended === true,
        text,
      },
    }
  }
  return {
    status: response.status,
    code: data?.code,
    message:
      response.status === 403 && !message
        ? 'دسترسی به این سرویس برای این کسب‌وکار مجاز نیست.'
        : serverText ?? fallback,
    field: typeof data?.field === 'string' && data.field ? data.field : undefined,
    amount: typeof data?.amount === 'number' ? data.amount : undefined,
    items: Array.isArray(data?.items) ? data?.items : undefined,
    balance: typeof data?.balance === 'number' ? data.balance : undefined,
    required: typeof data?.required === 'number' ? data.required : undefined,
  }
}

/** The service's tables are not migrated yet: a calm «به‌زودی» state, not an error page. */
export const isServiceUnavailable = (error: unknown): boolean => {
  const read = readServiceError(error, '')
  return read.code === SERVICE_UNAVAILABLE || read.status === 503
}

// ---- SMS cost ----------------------------------------------------------------------------------------------

/** SMS is closed while the administrator has set no price, unless the business does not pay for SMS. */
export const isSmsClosed = (sms: ServiceSms): boolean => sms.partPrice <= 0 && !sms.withoutCharge

/** parts × price × recipients; nothing when the business does not pay for SMS. */
export const estimateSmsCost = (sms: ServiceSms, parts: number, recipients: number): number =>
  sms.withoutCharge ? 0 : Math.max(0, parts) * Math.max(0, sms.partPrice) * Math.max(0, recipients)

// ---- digits and mobiles --------------------------------------------------------------------------------

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

export const latinDigits = (text: string): string =>
  (text || '')
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))

/** A count or an amount with Persian digits and grouping, as «لینک خریداران» writes them. */
export const fa = (value?: number | null): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : Math.trunc(value).toLocaleString('fa-IR')

/** A percentage with its decimals (۱٫۵٪ is never shown as ۱٪): what a business pays must read as it is charged. */
export const faPercent = (value?: number | null): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : value.toLocaleString('fa-IR', {maximumFractionDigits: 2})

/** How many days a subscription ending at <iso> has left (rounded up); undefined without an end. */
export const daysLeft = (iso?: string | null, now: number = Date.now()): number | undefined => {
  if (!iso) return undefined
  const end = Date.parse(iso)
  return Number.isFinite(end) ? Math.ceil((end - now) / 86_400_000) : undefined
}

/** A subscription may be renewed from its end within these days of it (BusinessServiceTariffs.RenewEarlyWindow). */
export const RENEW_EARLY_DAYS = 14

export const faDigits = (text: string | number): string =>
  String(text).replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)])

/**
 * An Iranian mobile as 09xxxxxxxxx, or undefined. Persian and Arabic digits, spaces and dashes are
 * accepted, and so are +98, 0098 and 98 in front.
 */
export const normalizeMobile = (text: string): string | undefined => {
  let value = latinDigits(text).replace(/[\s\-()]/g, '')
  if (value.startsWith('+98')) value = `0${value.slice(3)}`
  else if (value.startsWith('0098')) value = `0${value.slice(4)}`
  else if (/^98\d{10}$/.test(value)) value = `0${value.slice(2)}`
  else if (/^9\d{9}$/.test(value)) value = `0${value}`
  return /^09\d{9}$/.test(value) ? value : undefined
}

/** A percentage typed with any digits and a Persian or Latin decimal point (up to two decimals); undefined when it is not one. */
export const parsePercent = (text: string): number | undefined => {
  const value = latinDigits(text).replace(/[٫/]/g, '.').replace(/\s/g, '')
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(value)) return undefined
  const number = Number(value)
  return number > 0 && number <= 100 ? number : undefined
}

/** A whole number typed with any digits; undefined when it is not one. */
export const parseWhole = (text: string): number | undefined => {
  const value = latinDigits(text).replace(/[,،٬\s]/g, '')
  if (!/^\d+$/.test(value)) return undefined
  const number = Number(value)
  return Number.isSafeInteger(number) ? number : undefined
}
